import { getAdminOrganizations, type AdminOrganization } from '@urbanik/api-client';
import { StatePanel, canonicalStates } from '@urbanik/ui';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiBase } from './api-base';
import { useAccess } from './AccessBoundary';

export const activeOrganizationStorageKey = 'urbanik:active-organization';
type Value = Readonly<{
  organizations: readonly AdminOrganization[];
  active: AdminOrganization;
  select: (id: string) => void;
}>;
const Context = createContext<Value | null>(null);
export const useOrganization = () => useContext(Context);

export function OrganizationBoundary({ children }: { children: ReactNode }) {
  const access = useAccess();
  const requiresInstitutionalContext =
    access.permissions.includes('members.list.read') ||
    access.permissions.includes('collections.own_balance.read') ||
    access.permissions.includes('payments.own.create');
  const [organizations, setOrganizations] = useState<readonly AdminOrganization[] | null>(null);
  const [selectedId, setSelectedId] = useState(() =>
    localStorage.getItem(activeOrganizationStorageKey),
  );
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    getAdminOrganizations(apiBase, controller.signal)
      .then(({ items }) => {
        setOrganizations(items);
        if (!items.length) {
          setError(true);
          return;
        }
        setError(false);
        const selected = items.find((item) => item.id === selectedId);
        const fallback =
          items.find((item) => item.code === 'BASELINE_COMIVALLE') ?? items[0] ?? null;
        if (!selected && fallback) {
          localStorage.setItem(activeOrganizationStorageKey, fallback.id);
          setSelectedId(fallback.id);
        }
      })
      .catch((reason: unknown) => {
        if ((reason as { name?: string })?.name === 'AbortError') return;
        setError(true);
      });
    return () => controller.abort();
  }, [requiresInstitutionalContext]);
  const active = organizations?.find((item) => item.id === selectedId) ?? null;
  const value = useMemo<Value | null>(
    () =>
      active && organizations
        ? {
            active,
            organizations,
            select(id) {
              if (!organizations.some((item) => item.id === id)) return;
              localStorage.setItem(activeOrganizationStorageKey, id);
              setSelectedId(id);
              window.location.reload();
            },
          }
        : null,
    [active, organizations],
  );

  if (!requiresInstitutionalContext) return children;
  if (error)
    return (
      <StatePanel
        state={{
          ...canonicalStates.find((item) => item.kind === 'error')!,
          message: 'No se pudo establecer la entidad activa.',
        }}
        onAction={() => window.location.reload()}
      />
    );
  if (!value)
    return <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />;
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
